import { FileViewer } from "@/components/features";
import { Form } from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { CommunicationAttachment } from "@/components/ui/CommunicationSlideover/CommunicationAttachment";
import { SelectFileRepoPhotosSlideover } from "@/components/ui/CommunicationSlideover/SelectFileRepoPhotosSlideover";
import { SelectFormField } from "@/components/ui/FormFields/SelectFormField/SelectFormField";
import { TextAreaFormField } from "@/components/ui/FormFields/TextAreaFormField/TextAreaFormField";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner/LoadingSpinner";
import { Button } from "@/components/ui/shadcn/Button/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/shadcn/DropdownMenu/DropdownMenu";
import { Typography } from "@/components/ui/Typography/Typography";
import { useUser } from "@/components/UserProvider";
import { retrieveContactQueryOptions } from "@/lib/data/react-query";
import {
  retrieveContactSmsInfiniteQueryOptions,
  useCreateMessageMediaObject,
  useContactSmsLiveUpdates,
  useSendSms,
} from "@/lib/data/react-query/contacts/contact-sms";
import { getFirmVirtualBusinessCardsQueryOptions } from "@/lib/data/react-query/firm/firm-business-card";
import { getFirmPhonesQueryOptions } from "@/lib/data/react-query/firm/firm-phones";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { getItemFromPresignedUrlOptions } from "@/lib/data/react-query/s3";
import type { CONTACT_COMMUNICATION_STATUSES } from "@/lib/data/static/contactStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { ContactPhone } from "@/lib/types/Contact";
import type { FirmPhone, FirmVirtualBusinessCard } from "@/lib/types/Firm";
import type { MatterFile } from "@/lib/types/Matter";
import { cn } from "@/lib/utils/cn";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import {
  formatAddress,
  formatContactsName,
  formatDateTime,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useQuery,
  useQueryClient,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { startCase } from "lodash";
import {
  AlertCircleIcon,
  CheckCheckIcon,
  ClockIcon,
  IdCard,
  PaperclipIcon,
  XCircleIcon,
  XIcon,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface CommunicationSlideoverFormProps {
  selectedContactId: string;
  matterId?: string;
  defaultMessage?: string;
  onDirtyChange?: (isDirty: boolean) => void;
}

const textingFormSchema = z.object({
  message: z
    .string()
    .max(1600, "Message must be 1,600 characters or fewer.")
    .optional(),
});

const numbersFormSchema = z.object({
  phoneId: z.string().optional(),
  id: z.string().optional(),
});

const defaultTextingFormValues = {
  message: "",
};

const defaultNumbersFormValues = {
  phoneId: "",
  id: "",
};

const unavailableContactPhoneId = "unavailable-contact-phone";

type ContactCommunicationStatus =
  (typeof CONTACT_COMMUNICATION_STATUSES)[number];

const isFailedDeliveryStatus = (status?: ContactCommunicationStatus | null) =>
  status === "failed" || status === "undelivered";

const getFirmPhoneIdFromNumber = (firmPhones: FirmPhone[], number: string) => {
  return firmPhones.find((phone) => phone.number === number)?.id;
};

const getContactPhoneIdFromNumber = (
  contactPhones: ContactPhone[],
  number: string,
) => {
  return contactPhones.find((phone) => phone.number === number)?.id;
};

export const CommunicationSlideoverForm = React.memo(
  ({
    selectedContactId,
    matterId,
    defaultMessage,
    onDirtyChange,
  }: CommunicationSlideoverFormProps) => {
    const { data: matterData } = useQuery(
      retrieveMatterQueryOptions(matterId ?? ""),
    );
    const matter = matterData?.data;
    const { currentUser, hasActionPermission } = useUser();
    const queryClient = useQueryClient();
    const hasSendTextPermission = hasActionPermission(
      ACTION_PERMISSION_KEYS.contact,
      ACTION_PERMISSION_VALUES.create,
    );
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const textingForm = useForm<z.infer<typeof textingFormSchema>>({
      resolver: zodResolver(textingFormSchema),
      mode: "onChange",
      defaultValues: {
        message: defaultMessage ?? defaultTextingFormValues.message,
      },
    });
    const message = textingForm.watch("message") ?? "";

    useEffect(() => {
      void textingForm.trigger("message");
    }, [textingForm]);
    const [fileRepoSlideoverOpen, setFileRepoSlideoverOpen] = useState(false);
    const [filesToSend, setFilesToSend] = useState<MatterFile[]>([]);
    const [businessCardToSend, setBusinessCardToSend] =
      useState<FirmVirtualBusinessCard | null>(null);
    const [businessCardSelectionOpen, setBusinessCardSelectionOpen] =
      useState(false);

    const { data: firmPhones, isLoading: isFirmPhonesLoading } =
      useSuspenseQuery(getFirmPhonesQueryOptions());
    const { data: firmBusinessCards, isLoading: isBusinessCardsLoading } =
      useQuery(getFirmVirtualBusinessCardsQueryOptions());
    const { data: businessCardToSendData, isLoading: isBusinessCardLoading } =
      useQuery(
        getItemFromPresignedUrlOptions(
          "firm-file",
          businessCardToSend?.s3Key ?? "",
        ),
      );
    const createMessageMediaObject = useCreateMessageMediaObject();
    const { data: selectedContact } = useSuspenseQuery(
      retrieveContactQueryOptions(selectedContactId),
    );
    const {
      data: smsPages,
      fetchNextPage: fetchOlderMessages,
      hasNextPage: hasOlderMessages,
      isFetchingNextPage: isFetchingOlderMessages,
    } = useSuspenseInfiniteQuery(
      retrieveContactSmsInfiniteQueryOptions(selectedContactId),
    );
    useContactSmsLiveUpdates(
      selectedContactId,
      smsPages.pages[0]?.pagination?.liveUpdateCursor,
    );
    const sendSms = useSendSms();

    const communicationSms = useMemo(() => {
      const newestFirst = smsPages.pages.flatMap((page) => page.data.messages);
      const uniqueNewestFirst = [
        ...new Map(
          newestFirst.map((message) => [message.id, message]),
        ).values(),
      ];
      return uniqueNewestFirst.reverse();
    }, [smsPages.pages]);

    const mostRecentTextMessage =
      communicationSms[communicationSms.length - 1] ?? {};
    const mostRecentTextMessageContactNumber =
      mostRecentTextMessage.direction === "OUTBOUND"
        ? mostRecentTextMessage.toNumber
        : mostRecentTextMessage.fromNumber;
    const hasUnavailableMostRecentTextMessage =
      Boolean(mostRecentTextMessage.id) &&
      !getContactPhoneIdFromNumber(
        selectedContact.data.phones,
        mostRecentTextMessageContactNumber ?? "",
      );

    const numbersForm = useForm<z.infer<typeof numbersFormSchema>>({
      resolver: zodResolver(numbersFormSchema),
      values: mostRecentTextMessage.id
        ? mostRecentTextMessage.direction === "OUTBOUND"
          ? {
              phoneId: getFirmPhoneIdFromNumber(
                firmPhones.data,
                mostRecentTextMessage.fromNumber ?? "",
              ),
              id:
                getContactPhoneIdFromNumber(
                  selectedContact.data.phones,
                  mostRecentTextMessage.toNumber ?? "",
                ) ??
                (hasUnavailableMostRecentTextMessage
                  ? unavailableContactPhoneId
                  : undefined),
            }
          : {
              phoneId: getFirmPhoneIdFromNumber(
                firmPhones.data,
                mostRecentTextMessage.toNumber ?? "",
              ),
              id:
                getContactPhoneIdFromNumber(
                  selectedContact.data.phones,
                  mostRecentTextMessage.fromNumber ?? "",
                ) ??
                (hasUnavailableMostRecentTextMessage
                  ? unavailableContactPhoneId
                  : undefined),
            }
        : firmPhones.data.length && selectedContact.data.phones.length
          ? {
              phoneId: firmPhones.data[0].id,
              id: selectedContact.data.phones[0].id,
            }
          : defaultNumbersFormValues,
    });

    const selectedFirmPhone = firmPhones.data.find(
      (phone) => phone.id === numbersForm.watch("phoneId"),
    );
    const selectedContactPhoneId = numbersForm.watch("id");
    const selectedContactPhone = selectedContact.data.phones.find(
      (phone) => phone.id === selectedContactPhoneId,
    );
    const isReadOnlyConversation =
      selectedContactPhoneId === unavailableContactPhoneId;

    const contactPhoneItems = [
      ...(hasUnavailableMostRecentTextMessage
        ? [
            {
              label:
                formatLocalOrIntlPhoneNumber(
                  mostRecentTextMessageContactNumber ?? undefined,
                ) || "Unknown phone number",
              value: unavailableContactPhoneId,
              context: "No longer on contact",
            },
          ]
        : []),
      ...selectedContact.data.phones.map((phone) => ({
        label: `${formatLocalOrIntlPhoneNumber(phone)}${phone.isPrimary ? " (Primary)" : ""}`,
        value: phone.id,
        context: phone.label,
      })),
    ];

    const communicationsFilteredBySelectedPhones = useMemo(() => {
      if (isReadOnlyConversation) {
        return communicationSms;
      }

      return communicationSms.filter((communication) => {
        let fromNumber, toNumber;
        if (communication.direction === "OUTBOUND") {
          fromNumber = selectedFirmPhone?.number;
          toNumber = selectedContactPhone?.number;
        } else {
          fromNumber = selectedContactPhone?.number;
          toNumber = selectedFirmPhone?.number;
        }

        return (
          communication.fromNumber === fromNumber &&
          communication.toNumber === toNumber
        );
      });
    }, [
      communicationSms,
      isReadOnlyConversation,
      selectedFirmPhone,
      selectedContactPhone,
    ]);

    const scrollToBottom = () => {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop =
            messagesContainerRef.current.scrollHeight;
        }
      }, 100);
    };
    const latestCommunicationId =
      communicationsFilteredBySelectedPhones.at(-1)?.id;

    const handleSelectFileRepo = () => {
      setFileRepoSlideoverOpen(true);
    };

    const handleSaveSelectedFiles = (files: MatterFile[]) => {
      setFilesToSend(files);
      setFileRepoSlideoverOpen(false);
    };

    const handleSelectBusinessCard = (
      businessCard: FirmVirtualBusinessCard,
    ) => {
      setBusinessCardToSend(businessCard);
    };

    const handleRemoveBusinessCard = () => {
      setBusinessCardToSend(null);
    };

    useEffect(() => {
      scrollToBottom();
    }, [latestCommunicationId]);

    useEffect(() => {
      onDirtyChange?.(
        !isReadOnlyConversation &&
          (textingForm.formState.isDirty ||
            numbersForm.formState.isDirty ||
            filesToSend.length > 0 ||
            businessCardToSend !== null),
      );
    }, [
      businessCardToSend,
      filesToSend.length,
      isReadOnlyConversation,
      numbersForm.formState.isDirty,
      onDirtyChange,
      textingForm.formState.isDirty,
    ]);

    useEffect(() => {
      return () => {
        onDirtyChange?.(false);
      };
    }, [onDirtyChange]);

    const onSubmit = async (data: z.infer<typeof textingFormSchema>) => {
      if (
        isReadOnlyConversation ||
        !selectedContactPhone?.id ||
        !selectedFirmPhone?.id
      ) {
        return;
      }
      const handoffFiles = [];
      if (filesToSend.length > 0) {
        for (const file of filesToSend) {
          const fileUrlKey = queryClient.getQueryData<{
            url: string;
          }>([queryKeys.matterFileImages, file.presignedUrlKey]);
          if (!fileUrlKey) {
            return;
          }
          const fileRes = await fetch(fileUrlKey.url);
          const fileBlob = await fileRes.blob();
          const fileObject = new File([fileBlob], file.name, {
            type: fileBlob.type,
          });
          const handoffRes = await createMessageMediaObject.mutateAsync({
            file: fileObject,
          });
          handoffFiles.push({
            s3Key: handoffRes.key,
            contentType: fileBlob.type,
          });
        }
      }

      // Handle business cards
      if (businessCardToSendData) {
        const fileRes = await fetch(businessCardToSendData.url);
        const fileBlob = await fileRes.blob();
        const fileObject = new File(
          [fileBlob],
          `${businessCardToSend?.fullName ?? "Business Card"}.vcf`,
          {
            type: "text/x-vcard",
          },
        );
        const handoffRes = await createMessageMediaObject.mutateAsync({
          file: fileObject,
        });
        if (handoffRes.key) {
          handoffFiles.push({
            s3Key: handoffRes.key,
            contentType: "text/x-vcard",
          });
        }
      }
      await sendSms.mutateAsync({
        contactId: selectedContactId,
        phoneId: selectedContactPhone.id,
        firmPhone: selectedFirmPhone,
        message: data.message ?? "",
        user: currentUser,
        mediaFiles: handoffFiles,
      });
      setFilesToSend([]);
      setBusinessCardToSend(null);
      textingForm.reset(defaultTextingFormValues);
      scrollToBottom();
    };

    const isLoading =
      sendSms.isPending ||
      createMessageMediaObject.isPending ||
      isBusinessCardLoading ||
      isBusinessCardsLoading;

    const renderStatusIcon = (status?: ContactCommunicationStatus) => {
      if (!status) return null;
      switch (status) {
        case "queued":
          return <ClockIcon className="text-muted-foreground h-3 w-3" />;
        case "delivered":
          return <CheckCheckIcon className="text-muted-foreground h-3 w-3" />;
        case "undelivered":
          return <AlertCircleIcon className="text-destructive h-3 w-3" />;
        case "failed":
          return <XCircleIcon className="text-destructive h-3 w-3" />;
        case "received":
          return <CheckCheckIcon className="text-muted-foreground h-3 w-3" />;
        case "read":
          return <CheckCheckIcon className="text-muted-foreground h-3 w-3" />;
        default:
          return null;
      }
    };

    return (
      <div className="flex h-full grow flex-col overflow-hidden">
        <Form {...numbersForm}>
          <form className="shrink-0">
            <fieldset
              className={cn(slideoverFieldsetClassName, "m-0 mt-4 p-0 px-4")}
            >
              <SelectFormField
                label="Contact number"
                items={contactPhoneItems}
                register={numbersForm.register("id")}
                itemsKey="value"
                formatItemLabel={(item) => item?.label}
                formatItemContext={(item) => item?.context ?? undefined}
                formatItemContextClassName={(item) =>
                  item?.value === unavailableContactPhoneId
                    ? "text-destructive"
                    : undefined
                }
              />
              <SelectFormField
                label="Firm number"
                items={firmPhones.data.map((phone) => ({
                  label: formatLocalOrIntlPhoneNumber(phone),
                  value: phone.id,
                  context: phone.label,
                }))}
                isItemsLoading={isFirmPhonesLoading}
                register={numbersForm.register("phoneId")}
                itemsKey="value"
                formatItemLabel={(item) => item?.label}
                formatItemContext={(item) => item?.context}
                formatSelectedItemLabel={(item) =>
                  `${item?.label} (${item?.context})`
                }
              />
            </fieldset>
          </form>
        </Form>
        <div
          ref={messagesContainerRef}
          className="m-4 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto rounded-md border p-4"
        >
          {hasOlderMessages ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isFetchingOlderMessages}
              onClick={() => void fetchOlderMessages()}
            >
              {isFetchingOlderMessages ? "Loading…" : "Load older messages"}
            </Button>
          ) : null}
          {communicationsFilteredBySelectedPhones.length === 0 ? (
            <div className="flex h-full min-h-[160px] w-full items-center justify-center">
              <Typography variant="subHeading">
                No messages found for the selected firm and contact numbers.
              </Typography>
            </div>
          ) : (
            communicationsFilteredBySelectedPhones.map((communication) => {
              const hasFailedDelivery = isFailedDeliveryStatus(
                communication.status,
              );

              return (
                <div
                  key={communication.id}
                  className={cn(
                    "flex w-full",
                    communication.direction === "OUTBOUND" && "justify-end",
                  )}
                >
                  {communication.direction === "OUTBOUND" ? (
                    <div
                      key={communication.id}
                      className="flex h-fit w-fit max-w-3/4 flex-col items-end justify-end gap-1"
                    >
                      <Typography variant="subHeading" size="xs">
                        {`${communication.senderSnapshot?.firstName ?? "Unknown sender"} ${communication.senderSnapshot?.lastName ?? ""} • ${formatDateTime(
                          convertToLocalFromDatabaseDateTime(
                            communication.dateCreated,
                          ),
                          false,
                        )}`}
                      </Typography>
                      {communication.s3Keys?.map((s3Key) => (
                        <CommunicationAttachment
                          key={s3Key}
                          s3Key={s3Key}
                          direction="OUTBOUND"
                        />
                      ))}
                      {communication.message && (
                        <div
                          className={cn(
                            "w-fit justify-end rounded-2xl border p-2",
                            hasFailedDelivery
                              ? "border-destructive/30 bg-destructive/10"
                              : "bg-blue-100",
                          )}
                        >
                          <Typography
                            className={cn(
                              "w-fit whitespace-pre-wrap",
                              hasFailedDelivery
                                ? "text-destructive"
                                : "text-blue-950",
                            )}
                          >
                            {communication.message}
                          </Typography>
                        </div>
                      )}
                      {communication.status && (
                        <div className="mt-0.5 flex items-center gap-1">
                          {renderStatusIcon(communication.status)}
                          <Typography
                            size="xs"
                            className={
                              hasFailedDelivery
                                ? "text-destructive"
                                : "text-muted-foreground"
                            }
                          >
                            {startCase(communication.status)}
                          </Typography>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      key={communication.id}
                      className="flex h-fit w-fit max-w-3/4 flex-col justify-end gap-1"
                    >
                      <Typography variant="subHeading" size="xs">
                        {`${formatContactsName(selectedContact.data)} • 
                      ${formatDateTime(
                        convertToLocalFromDatabaseDateTime(
                          communication.dateCreated,
                        ),
                        false,
                      )}`}
                      </Typography>
                      {communication.s3Keys?.map((s3Key) => (
                        <CommunicationAttachment
                          key={s3Key}
                          s3Key={s3Key}
                          matterId={matter?.id}
                          direction="INBOUND"
                        />
                      ))}
                      {communication.message && (
                        <div className="bg-card w-fit rounded-2xl border p-2">
                          <Typography className="w-fit whitespace-pre-wrap">
                            {communication.message}
                          </Typography>
                        </div>
                      )}
                      {communication.status && (
                        <div className="mt-0.5 flex items-center gap-1">
                          {renderStatusIcon(communication.status)}
                          <Typography
                            size="xs"
                            className="text-muted-foreground"
                          >
                            {startCase(communication.status)}
                          </Typography>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <Form {...textingForm}>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await textingForm.handleSubmit(onSubmit)();
            }}
            className="flex shrink-0 flex-col"
          >
            <fieldset
              disabled={isReadOnlyConversation || !hasSendTextPermission}
              className={cn(slideoverFieldsetClassName, "m-0")}
            >
              <div className="col-span-full">
                {isReadOnlyConversation && (
                  <Typography className="text-muted-foreground text-sm">
                    You can view this conversation, but you can’t send messages
                    because this number is no longer on the contact.
                  </Typography>
                )}
                <TextAreaFormField
                  register={textingForm.register("message")}
                  label="Message"
                />
                <div className="mt-1 flex items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" type="button">
                          <PaperclipIcon className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem
                          disabled={!matter?.matterId}
                          onClick={handleSelectFileRepo}
                        >
                          <Typography>From file repo</Typography>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={
                            !firmBusinessCards?.data ||
                            firmBusinessCards.data.length === 0
                          }
                          onClick={() => {
                            setBusinessCardSelectionOpen(true);
                          }}
                        >
                          <IdCard className="mr-2 h-4 w-4" />
                          <Typography>Business card</Typography>
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled>
                          <Typography>Upload file</Typography>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {filesToSend.length > 0 && (
                      <div className="flex min-w-0">
                        {filesToSend.map((file) => (
                          <div
                            key={file.id}
                            className="group relative flex items-end"
                          >
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="absolute top-0 right-2 h-4 w-4 rounded-full bg-white opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                              onClick={() => {
                                setFilesToSend(
                                  filesToSend.filter((f) => f.id !== file.id),
                                );
                              }}
                            >
                              <XIcon className="h-2 w-2 text-xs" />
                            </Button>
                            <FileViewer
                              fileType="matter-file"
                              file={{
                                fileName: file.name,
                                presignedURLKey: file.presignedUrlKey || "",
                              }}
                              thumbnail={true}
                              showDownload={false}
                              showTitle={false}
                              imageClassName="w-10 h-10 border"
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {businessCardToSend && (
                      <div className="group relative flex min-w-0 items-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute top-0 right-2 h-4 w-4 rounded-full bg-white opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                          onClick={handleRemoveBusinessCard}
                        >
                          <XIcon className="h-2 w-2 text-xs" />
                        </Button>
                        <div className="bg-muted/30 flex h-10 min-w-0 items-center gap-2 rounded-md border px-2">
                          <IdCard className="text-muted-foreground h-5 w-5 shrink-0" />
                          <div className="flex min-w-0 flex-col justify-center">
                            <Typography className="truncate text-xs font-medium">
                              {businessCardToSend.fullName}
                            </Typography>
                            <Typography className="text-muted-foreground text-xs">
                              Business card
                            </Typography>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <Button
                    type="submit"
                    disabled={
                      (message.length === 0 &&
                        filesToSend.length === 0 &&
                        !businessCardToSend) ||
                      message.length > 1600 ||
                      isReadOnlyConversation ||
                      isLoading
                    }
                  >
                    {isLoading ? <LoadingSpinner /> : "Send"}
                  </Button>
                </div>
              </div>
            </fieldset>
          </form>
        </Form>
        <SelectFileRepoPhotosSlideover
          matter={matter}
          open={fileRepoSlideoverOpen}
          setOpen={setFileRepoSlideoverOpen}
          onSaveSelectedFiles={handleSaveSelectedFiles}
        />
        <AppModal
          title="Select business card"
          open={businessCardSelectionOpen}
          onOpenChange={setBusinessCardSelectionOpen}
          size="2xl"
          overlayClassName="z-[116] bg-black/50 backdrop-blur-md"
          className="z-[117] max-h-[min(80vh,720px)]"
        >
          {isBusinessCardsLoading ? (
            <div className="flex min-h-40 items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : !firmBusinessCards?.data ||
            firmBusinessCards.data.length === 0 ? (
            <div className="flex min-h-40 items-center justify-center">
              <Typography variant="subHeading">
                No business cards available.
              </Typography>
            </div>
          ) : (
            <div className="space-y-4">
              {firmBusinessCards.data.map((businessCard) => {
                const formattedPhoneNumber = businessCard.phoneNumber
                  ? formatLocalOrIntlPhoneNumber({
                      number: businessCard.phoneNumber,
                    })
                  : null;

                const formattedAddress = formatAddress({
                  street1: businessCard.street1,
                  street2: businessCard.street2,
                  city: businessCard.city,
                  state: businessCard.state,
                  zipCode: businessCard.zipCode,
                  country: businessCard.country,
                });

                return (
                  <button
                    key={businessCard.id}
                    type="button"
                    className="hover:bg-muted/50 grid w-full cursor-pointer grid-cols-[auto_minmax(0,1fr)] items-center gap-3 overflow-hidden rounded-lg border p-4 text-left"
                    onClick={() => {
                      handleSelectBusinessCard(businessCard);
                      setBusinessCardSelectionOpen(false);
                    }}
                  >
                    <div className="bg-muted flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
                      <IdCard className="text-muted-foreground h-5 w-5" />
                    </div>
                    <div className="min-w-0 overflow-hidden">
                      <Typography className="line-clamp-3 font-medium">
                        {businessCard.fullName}
                      </Typography>
                      {formattedPhoneNumber && (
                        <Typography
                          size="sm"
                          className="text-muted-foreground line-clamp-3"
                        >
                          {formattedPhoneNumber}
                        </Typography>
                      )}
                      {businessCard.email && (
                        <p className="text-muted-foreground block max-w-full overflow-hidden text-sm break-normal text-ellipsis whitespace-nowrap">
                          {businessCard.email}
                        </p>
                      )}
                      {formattedAddress && (
                        <p className="text-muted-foreground block max-w-full overflow-hidden text-sm break-normal text-ellipsis whitespace-nowrap">
                          {formattedAddress}
                        </p>
                      )}
                      {businessCard.website && (
                        <p className="text-muted-foreground block max-w-full truncate overflow-hidden text-sm break-normal text-ellipsis whitespace-nowrap">
                          {businessCard.website}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </AppModal>
      </div>
    );
  },
);

CommunicationSlideoverForm.displayName = "CommunicationSlideoverForm";
